// @ts-strict-ignore
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Subject, Subscription } from 'rxjs';
import { Service } from 'rainbow-web-sdk/services/service';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';

export const I18N_SVC = 'I18nService';

const dateFnsLocales = {
    'en-US': async () => await import('date-fns/locale/en-US'),
    'fr': async () => await import('date-fns/locale/fr'),
    'ca': async () => await import('date-fns/locale/ca'),
    'cs': async () => await import('date-fns/locale/cs'),
    'de': async () => await import('date-fns/locale/de'),
    'es': async () => await import('date-fns/locale/es'),
    'fi': async () => await import('date-fns/locale/fi'),
    'it': async () => await import('date-fns/locale/it'),
    'he': async () => await import('date-fns/locale/he'),
    'ja': async () => await import('date-fns/locale/ja'),
    'ko': async () => await import('date-fns/locale/ko'),
    'nl': async () => await import('date-fns/locale/nl'),
    'nn': async () => await import('date-fns/locale/nn'),
    'pt-BR': async () => await import('date-fns/locale/pt-BR'),
    'pt': async () => await import('date-fns/locale/pt'),
    'pl': async () => await import('date-fns/locale/pl'),
    'ru': async () => await import('date-fns/locale/ru'),
    'sv': async () => await import('date-fns/locale/sv'),
    'tr': async () => await import('date-fns/locale/tr'),
    'zh-CN': async () => await import('date-fns/locale/zh-CN'),
};

export class I18nService extends Service {
    public isInitialized: boolean = false;

    private subject: Subject<void> = new Subject<void>();
    private translator: any;
    private settingsService: SettingsService = SettingsService.getInstance();
    private loggerService: LoggerService = LoggerService.getInstance();

    public static getInstance() { return Service.create(I18N_SVC, I18nService); }
    public static build() { return new I18nService(); }

    private constructor() {
        super();
        this.settingsService.events('LANGUAGE_UPDATED').subscribe(() => { this.updateTranslationLanguage(); });
    }

    public subscribe(observable: any): Subscription { return this.subject.subscribe(observable); }

    public async configure(translator: any) {
        this.translator = translator;
        await this.updateTranslationLanguage();
    }

    public translate(label: string, params: any = null): string {
        if (typeof this.translator === 'object') {
            //do not translate labels that contain spaces (they're already translated)
            if (label?.indexOf && label?.indexOf(" ") !== -1) return label;

            if (this.translator.translate) {
                try { return this.translator.translate(label, params || {}); }
                catch (error) {
                    this.loggerService.error(`[I18nService] translate(${label}, ${params}) failure`);
                    return label;
                }
            }
            return this.translator.instant(label, params || {});
        }
        return label;
    }

    public getCurrentLanguage() {
        let result = 'en';
        if (this.translator) {
            result = this.translator.getActiveLang ? this.translator.getActiveLang() : this.translator.use();
        }
        return result;
    }

    private async updateTranslationLanguage(): Promise<void> {
        try {
            const currentLanguage = this.settingsService.getAppliLanguage();
            if (this.translator) {
                if (this.translator.translate) {
                    // Wait for transloco
                    this.translator.load(currentLanguage.code).subscribe(() => {
                        this.loggerService.info(`[I18nService] updateTranslationLanguage -- "${currentLanguage.code}" labels are loaded`);
                        this.isInitialized = true;
                        this.translator.setActiveLang(currentLanguage.code);
                        this.subject.next();
                    });
                }
                else {
                    const loadedLanguage = await this.translator.use(currentLanguage.code);
                    this.loggerService.info(`[I18nService] updateTranslationLanguage -- "${loadedLanguage}" labels are loaded`);
                    this.isInitialized = true;
                    this.subject.next();
                }
            }

            if (moment) moment.locale(currentLanguage.codeMoment);

            const locale = dateFnsLocales[currentLanguage.codeDateFns] === undefined
                ? dateFnsLocales['en-US']
                : dateFnsLocales[currentLanguage.codeDateFns];

            setDefaultOptions({
                locale: (await locale()).default
            });
        }
        catch (error) {
            this.loggerService.error(`[I18nService] updateTranslationLanguage -- failure -- ${error.message}`);
        }
    }

}
